import logger from '@/lib/logger';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Barbell, Fire, TrendUp, ChartLineUp, FunnelSimple } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_TOOLTIP_STYLE = { background: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0, color: '#fff', fontFamily: 'DM Sans' };
const CHART_CURSOR = { fill: 'rgba(255,255,255,0.05)' };
const CHART_TICK = { fill: '#71717A', fontSize: 10 };
const MOOD_TOOLTIP_STYLE = { background: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0, color: '#fff' };
const MOOD_DOT_RED = { fill: '#FF3B30', r: 3 };
const BAR_RADIUS = [2, 2, 0, 0];
const MOOD_Y_DOMAIN = [1, 5];

export default function WorkoutHistory() {
  const [history, setHistory] = useState([]);
  const [moodHistory, setMoodHistory] = useState([]);
  const [streak, setStreak] = useState({});
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [histRes, moodRes, streakRes] = await Promise.all([
        axios.get(`${API}/workouts/history`, { params: { days } }),
        axios.get(`${API}/mood/history`, { params: { days } }),
        axios.get(`${API}/workouts/streak`),
      ]);
      setHistory(histRes.data);
      setMoodHistory(moodRes.data);
      setStreak(streakRes.data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Group by date
  const grouped = {};
  history.forEach(h => {
    if (!grouped[h.date]) grouped[h.date] = [];
    grouped[h.date].push(h);
  });
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  // Chart data (last 14 days)
  const chartData = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    const dayWorkouts = grouped[dateStr] || [];
    const totalMin = dayWorkouts.reduce((s, w) => s + (w.duration_minutes || 0), 0);
    chartData.push({
      date: dateStr,
      day: d.toLocaleDateString('en', { weekday: 'short' }).toUpperCase(),
      minutes: totalMin,
      count: dayWorkouts.length
    });
  }

  const totalMinutes = history.reduce((s, h) => s + (h.duration_minutes || 0), 0);
  const totalExercises = history.reduce((s, h) => s + (h.exercises_completed || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="workout-history-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">Progress</div>
        <h1 className="text-4xl font-black tracking-tighter">WORKOUT HISTORY</h1>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="tactical-card p-5">
          <Fire weight="fill" className="w-7 h-7 text-[#FF3B30] mb-2" />
          <div className="text-2xl font-black font-['Barlow_Condensed']">{streak.current_streak || 0}</div>
          <div className="label-tactical mt-1">Current Streak</div>
        </div>
        <div className="tactical-card p-5">
          <Barbell weight="bold" className="w-7 h-7 text-[#007AFF] mb-2" />
          <div className="text-2xl font-black font-['Barlow_Condensed']">{history.length}</div>
          <div className="label-tactical mt-1">Workouts ({days}d)</div>
        </div>
        <div className="tactical-card p-5">
          <Clock weight="bold" className="w-7 h-7 text-[#34C759] mb-2" />
          <div className="text-2xl font-black font-['Barlow_Condensed']">{totalMinutes}</div>
          <div className="label-tactical mt-1">Total Minutes</div>
        </div>
        <div className="tactical-card p-5">
          <TrendUp weight="bold" className="w-7 h-7 text-[#FF9500] mb-2" />
          <div className="text-2xl font-black font-['Barlow_Condensed']">{totalExercises}</div>
          <div className="label-tactical mt-1">Exercises Done</div>
        </div>
      </div>

      {/* Time Range Filter */}
      <div className="flex items-center gap-3 mb-6">
        <FunnelSimple weight="bold" className="w-4 h-4 text-[#71717A]" />
        <span className="label-tactical">Period</span>
        {[7, 14, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)} data-testid={`history-days-${d}`}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${days === d ? 'bg-[#FF3B30] text-white' : 'bg-[#141414] border border-white/10 text-[#A1A1AA] hover:border-white/20'}`}>
            {d}d
          </button>
        ))}
      </div>

      {/* Activity Chart */}
      <div className="tactical-card p-6 mb-6" data-testid="history-chart">
        <h3 className="text-xl font-bold tracking-tight mb-4">ACTIVITY OVERVIEW</h3>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="day" axisLine={false} tickLine={false} tick={CHART_TICK} />
              <YAxis hide />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                cursor={CHART_CURSOR}
                formatter={(value) => [`${value} min`, 'Activity']}
              />
              <Bar dataKey="minutes" fill="#FF3B30" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Mood Trend */}
      {moodHistory.length > 1 && (
        <div className="tactical-card p-6 mb-6" data-testid="mood-trend">
          <h3 className="text-xl font-bold tracking-tight mb-4">MOOD TREND</h3>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={moodHistory.slice().reverse().map(m => ({
                ...m,
                day: new Date(m.date + 'T12:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' })
              }))}>
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={CHART_TICK} />
                <YAxis domain={MOOD_Y_DOMAIN} hide />
                <Tooltip contentStyle={MOOD_TOOLTIP_STYLE} />
                <Line type="monotone" dataKey="mood" stroke="#FF3B30" strokeWidth={2} dot={MOOD_DOT_RED} name="Mood" />
                <Line type="monotone" dataKey="energy" stroke="#007AFF" strokeWidth={2} dot={MOOD_DOT_BLUE} name="Energy" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-2">
            <span className="text-xs text-[#FF3B30] flex items-center gap-1"><span className="w-3 h-0.5 bg-[#FF3B30] inline-block" /> Mood</span>
            <span className="text-xs text-[#007AFF] flex items-center gap-1"><span className="w-3 h-0.5 bg-[#007AFF] inline-block" /> Energy</span>
          </div>
        </div>
      )}

      {/* Workout Log */}
      <h3 className="text-xl font-bold tracking-tight mb-4">WORKOUT LOG</h3>
      {sortedDates.length === 0 ? (
        <div className="tactical-card p-8 text-center">
          <Barbell weight="bold" className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
          <p className="text-[#71717A]">No workouts recorded in this period.</p>
          <button onClick={() => navigate('/workouts')} className="btn-primary px-6 py-2.5 mt-4 text-sm">
            Start a Workout
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedDates.map(date => {
            const dayWorkouts = grouped[date];
            const dayTotal = dayWorkouts.reduce((s, w) => s + (w.duration_minutes || 0), 0);
            const isToday = date === new Date().toISOString().split('T')[0];
            return (
              <div key={date} className="tactical-card p-5" data-testid={`history-date-${date}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar weight="bold" className="w-4 h-4 text-[#FF3B30]" />
                    <span className="font-bold text-sm">
                      {isToday ? 'TODAY' : new Date(date + 'T12:00:00').toLocaleDateString('en', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <span className="text-xs text-[#71717A]">{dayTotal} min total</span>
                </div>
                <div className="space-y-2">
                  {dayWorkouts.map((w, i) => (
                    <div key={`completion-${w.completed_at}`} className="flex items-center justify-between bg-[#1C1C1E] border border-white/5 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Barbell weight="bold" className="w-4 h-4 text-[#34C759]" />
                        <span className="text-sm">{w.exercises_completed} exercise{w.exercises_completed !== 1 ? 's' : ''}</span>
                      </div>
                      <span className="text-xs text-[#A1A1AA] flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {w.duration_minutes} min
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
